import { VALTYPES } from '../../../../data/interfaces';

export const ARITY = {
  SINGLE: 'single',
  VECTOR: 'vector',
};

type TpETLExprFunctionArgumentProcessor = (args: any[]) => { dataType: string; arity: string };

export interface TpETLExpressionFunctionDef {
  name: string;
  signature: string;
  description: string;
  processArguments: TpETLExprFunctionArgumentProcessor;
}

export const etlExpressionFunctionList: TpETLExpressionFunctionDef[] = [
  {
    name: 'percentile',
    signature: 'percentile(column, percent)',
    description: 'Takes a numerical column and returns a specified percentile as a single numerical value.',
    processArguments: (args: any[]) => {
      if (args.length !== 2) throw 'Expected 2 arguments';
      if (args[0].dataType !== VALTYPES.NUMBER || args[0].arity != ARITY.VECTOR)
        throw 'Argument 1 should be a numerical column';
      if (args[1].dataType !== VALTYPES.NUMBER || args[1].arity != ARITY.SINGLE)
        throw 'Argument 2 should be a numerical value';
      return {
        dataType: VALTYPES.NUMBER,
        arity: ARITY.SINGLE,
      };
    },
  },

  {
    name: 'rank',
    signature: 'rank(column)',
    description:
      'Takes a numerical column and returns a new numerical column with the rank of each row value (zero based).',
    processArguments: (args: any[]) => {
      if (args.length !== 1) throw 'Expected 1 argument';
      if (args[0].dataType !== VALTYPES.NUMBER || args[0].arity != ARITY.VECTOR)
        throw 'Argument should be a numerical column';
      return {
        dataType: VALTYPES.NUMBER,
        arity: ARITY.SINGLE,
      };
    },
  },

  {
    name: 'normalize',
    signature: 'normalize(column)',
    description:
      'Takes a numerical column and returns a new numerical column with the normalized value of each row value (i.e. average subtracted and divided by standard deviation).',
    processArguments: (args: any[]) => {
      if (args.length !== 1) throw 'Expected 1 argument';
      if (args[0].dataType !== VALTYPES.NUMBER || args[0].arity != ARITY.VECTOR)
        throw 'Argument should be a numerical column';
      return {
        dataType: VALTYPES.NUMBER,
        arity: ARITY.SINGLE,
      };
    },
  },

  {
    name: 'min',
    signature: 'min(val1, val2)',
    description: 'Returns the minimum of two values. Arguments can be individual values or columns.',
    processArguments: (args: any[]) => {
      if (args.length !== 2) throw 'Expected 2 arguments';
      if (args[0].dataType !== VALTYPES.NUMBER || args[1].dataType !== VALTYPES.NUMBER)
        throw 'Arguments should be numerical';
      return {
        dataType: VALTYPES.NUMBER,
        arity: args[0].arity == ARITY.VECTOR || args[1].arity == ARITY.VECTOR ? ARITY.VECTOR : ARITY.SINGLE,
      };
    },
  },

  {
    name: 'max',
    signature: 'max(val1, val2)',
    description: 'Returns the maximum of two values. Arguments can be individual values or columns.',
    processArguments: (args: any[]) => {
      if (args.length !== 2) throw 'Expected 2 arguments';
      if (args[0].dataType !== VALTYPES.NUMBER || args[1].dataType !== VALTYPES.NUMBER)
        throw 'Arguments should be numerical';
      return {
        dataType: VALTYPES.NUMBER,
        arity: args[0].arity == ARITY.VECTOR || args[1].arity == ARITY.VECTOR ? ARITY.VECTOR : ARITY.SINGLE,
      };
    },
  },

  {
    name: 'log10',
    signature: 'log10(column)',
    description: 'Returns log with base 10 values.',
    processArguments: (args: any[]) => {
      if (args.length !== 1) throw 'Expected 1 argument';
      if (args[0].dataType !== VALTYPES.NUMBER) throw 'Argument should be numerical';
      return {
        dataType: VALTYPES.NUMBER,
        arity: args[0].arity,
      };
    },
  },

  {
    name: 'row_number',
    signature: 'row_number()',
    description: 'Returns the row number (one based) as a new string column.',
    processArguments: (args: any[]) => {
      if (args.length !== 0) throw 'Expected 0 arguments';
      return {
        dataType: VALTYPES.STRING,
        arity: ARITY.VECTOR,
      };
    },
  },
];
